import axios from 'axios';
import qs from 'qs';
import * as auth from '../services/auth/auth.service';

const baseAxiosConfig = {
    // eslint-disable-next-line no-undef
    baseURL: window.location.protocol + '//' + window.location.host,
    timeout: 10000,
    // timeout: 2000, if you want to test axios cancelled, use this timeout value.
    headers: {
        Accept: 'application/json',
    },
    xsrfHeaderName: 'X-XSRF-TOKEN', //have to add this, else axios will not automatically pass XSRF-TOKEN cookie to X-XSRF-TOKEN header when making request.
};
const BackendCodeAPI = axios.create(baseAxiosConfig);

BackendCodeAPI.defaults.headers.Authorization = `Bearer ${auth.getToken()}`;

/**
 * @description These pieces allow us to throw errors on connection timeouts
 * @see https://github.com/axios/axios/issues/647#issuecomment-459517694
 * @returns {{ abort: { token: string, cancel: function }, connectionTimeout: setTimeout }}
 */
const getRequestAbortionPieces = () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), baseAxiosConfig.timeout);
    return { controller, timeoutId };
};

/**
 * @param {string} path
 * @param {?Object.<string, any>} params URL parameters to include in the query string
 * @returns {Promise<AxiosPromise<any>>}
 */
export const get = async (path, params = {}) => {
    const { controller, timeoutId } = getRequestAbortionPieces();
    return BackendCodeAPI.get(`/${path}`, {
        signal: controller.signal,
        params: params,
        /**
         * @description paramsSerializer takes an array of query params that is usually
         * serialized like this '/api/?id[]=1&id[]=2' and converts it into '/api/?id=1&id=2'
         * to better work with the API
         * */
        paramsSerializer: (parameters_) => qs.stringify(parameters_, { arrayFormat: 'indices' }),
    })
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            auth.handleException(error, path);
        })
        .finally(() => {
            clearTimeout(timeoutId);
        });
};

/**
 * @param {string} path
 * @param {?Object.<string, any>} body
 * @returns {Promise<AxiosResponse<any>>}
 */
export const post = async (path, body = {}, config) => {
    const { controller, timeoutId } = getRequestAbortionPieces();
    return BackendCodeAPI.post(`/${path}`, body, {
        ...config,
        signal: controller.signal,
    })
        .then((response) => {
            return response.data;
        })
        .catch(async (error) => {
            auth.handleException(error, path);
        })
        .finally(() => {
            clearTimeout(timeoutId);
        });
};

/**
 * @param {string} path
 * @param {?Object.<string, any>} body
 * @returns {Promise<AxiosResponse<any>>}
 */
export const put = async (path, body = {}) => {
    const { controller, timeoutId } = getRequestAbortionPieces();
    return BackendCodeAPI.put(`/${path}`, body, {
        signal: controller.signal,
    })
        .then((response) => {
            return response.data;
        })
        .catch(async (error) => {
            auth.handleException(error, path);
        })
        .finally(() => {
            clearTimeout(timeoutId);
        });
};

/**
 * @param {string} path
 * @param {?Object.<string, any>} body
 * @returns {Promise<AxiosResponse<any>>}
 */
export const patch = async (path, body = {}) => {
    const { controller, timeoutId } = getRequestAbortionPieces();
    return BackendCodeAPI.patch(`/${path}`, body, {
        signal: controller.signal,
    })
        .then((response) => {
            return response.data;
        })
        .catch(async (error) => {
            auth.handleException(error, path);
        })
        .finally(() => {
            clearTimeout(timeoutId);
        });
};

/**
 * @param {string} path
 * @param {?Object.<string, any>} params URL parameters to include in the query string
 * @returns {Promise<AxiosPromise<any>>}
 */
export const destroy = async (path, params = {}) => {
    const { controller, timeoutId } = getRequestAbortionPieces();
    return BackendCodeAPI.delete(`/${path}`, {
        signal: controller.signal,
        params: params,
        /**
         * @description paramsSerializer takes an array of query params that is usually
         * serialized like this '/api/?id[]=1&id[]=2' and converts it into '/api/?id=1&id=2'
         * to better work with the API
         * */
        paramsSerializer: (parameters_) => qs.stringify(parameters_, { arrayFormat: 'indices' }),
    })
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            auth.handleException(error, path);
        })
        .finally(() => {
            clearTimeout(timeoutId);
        });
};
